import { motion } from "framer-motion";

/**
 * LivingDoodles — small illustrated elements that pop in / grow /
 * wiggle / disappear on cycles, giving the hero a hand-inked, alive
 * quality without ever becoming permanent decoration.
 * All elements have `pointer-events: none` and low z-index so they
 * never interfere with copy or interactions.
 */

const IconSun = ({ size = 72 }) => (
  <svg width={size} height={size} viewBox="0 0 80 80" fill="none" aria-hidden="true">
    <g stroke="#FF6B2C" strokeWidth="1.6" strokeLinecap="round">
      <circle cx="40" cy="40" r="14" />
      <path d="M40 10 L40 18" />
      <path d="M40 62 L40 70" />
      <path d="M10 40 L18 40" />
      <path d="M62 40 L70 40" />
      <path d="M19 19 L25 25" />
      <path d="M55 55 L61 61" />
      <path d="M19 61 L25 55" />
      <path d="M55 25 L61 19" />
    </g>
  </svg>
);

const IconYarnBall = ({ size = 42 }) => (
  <svg width={size} height={size} viewBox="0 0 42 42" fill="none" aria-hidden="true">
    <circle cx="21" cy="21" r="15" stroke="#FF6B2C" strokeWidth="1.4" fill="#FFEBDF" />
    <g stroke="#B84A2A" strokeWidth="1" fill="none" strokeLinecap="round">
      <path d="M9 15 Q 21 8, 33 15" />
      <path d="M8 21 Q 21 14, 34 21" />
      <path d="M9 27 Q 21 20, 33 27" />
      <path d="M11 33 Q 21 26, 31 33" />
      <path d="M14 10 Q 21 4, 28 10" />
    </g>
    <path d="M35 25 Q 42 26, 40 34" stroke="#B84A2A" strokeWidth="1" fill="none" strokeLinecap="round" />
  </svg>
);

const IconSprout = ({ size = 56 }) => (
  <svg width={size} height={size} viewBox="0 0 56 72" fill="none" aria-hidden="true">
    <g stroke="#3E5B3A" strokeWidth="1.6" strokeLinecap="round" fill="none">
      <path d="M28 68 L28 30" />
      <path d="M28 42 Q 12 40, 12 26 Q 22 26, 28 38" fill="#B7D0A6" />
      <path d="M28 34 Q 44 32, 44 18 Q 34 18, 28 30" fill="#B7D0A6" />
      <path d="M28 30 Q 28 20, 32 12" />
    </g>
  </svg>
);

const IconLeaf = ({ size = 42, tint = "#5F7A5A" }) => (
  <svg width={size} height={size * 1.4} viewBox="0 0 30 42" fill="none" aria-hidden="true">
    <path d="M15 40 C 4 32, 4 12, 15 2 C 26 12, 26 32, 15 40 Z" stroke={tint} strokeWidth="1.4" fill="rgba(95,122,90,0.14)" />
    <path d="M15 4 L 15 40" stroke={tint} strokeWidth="1" />
  </svg>
);

const IconCatTail = ({ size = 90 }) => (
  <svg width={size} height={size} viewBox="0 0 90 90" fill="none" aria-hidden="true">
    <path
      d="M 6 82 C 30 78, 46 60, 46 40 C 46 24, 60 20, 70 30 C 74 34, 74 42, 68 44"
      stroke="#111"
      strokeWidth="10"
      strokeLinecap="round"
      fill="none"
    />
  </svg>
);

const IconMapPin = ({ size = 32 }) => (
  <svg width={size} height={size * 1.3} viewBox="0 0 32 42" fill="none" aria-hidden="true">
    <path d="M16 40 C 8 30, 4 22, 4 15 A 12 12 0 0 1 28 15 C 28 22, 24 30, 16 40 Z"
          stroke="#111" strokeWidth="1.6" fill="#FAF8F5" />
    <circle cx="16" cy="15" r="4" fill="#FF6B2C" />
  </svg>
);

const IconCloud = ({ size = 100 }) => (
  <svg width={size} height={size * 0.5} viewBox="0 0 100 50" fill="none" aria-hidden="true">
    <path d="M14 40 C 4 40, 4 26, 16 26 C 18 16, 34 14, 40 24 C 46 18, 62 20, 62 32 C 72 30, 82 36, 78 44 L 14 44 Z"
          stroke="#111" strokeWidth="1.3" fill="none" opacity="0.6" />
  </svg>
);

// Popping animation preset — pops in, holds, wiggles, pops out, loops
const popCycle = (dur = 6, hold = 2.4, delay = 0) => ({
  animate: {
    opacity: [0, 1, 1, 0],
    scale: [0.4, 1, 1, 0.75],
  },
  transition: {
    duration: dur,
    times: [0, 0.15, 0.15 + hold / dur, 1],
    delay,
    repeat: Infinity,
    repeatDelay: 4 + Math.random() * 4,
    ease: [0.34, 1.56, 0.64, 1],
  },
});

const wiggleCycle = (dur = 5, delay = 0) => ({
  animate: {
    opacity: [0, 1, 1, 0],
    rotate: [-8, 4, -6, 8],
    y: [0, -6, 4, 0],
  },
  transition: {
    duration: dur,
    delay,
    repeat: Infinity,
    repeatDelay: 3 + Math.random() * 3,
    ease: "easeInOut",
  },
});

export default function LivingDoodles() {
  // Each doodle: a position + one of the animation cycles.
  const doodles = [
    // Growing sprouts
    { el: <IconSprout />, style: { bottom: "8%", left: "3%" }, anim: popCycle(7, 3, 0.4) },
    { el: <IconSprout size={44} />, style: { bottom: "6%", right: "6%" }, anim: popCycle(8, 3.5, 3) },

    // Sun rotating
    {
      el: <IconSun size={72} />,
      style: { top: "12%", left: "42%" },
      anim: {
        animate: {
          opacity: [0, 1, 1, 0],
          rotate: [0, 40, 80, 120],
          scale: [0.6, 1, 1, 0.9],
        },
        transition: { duration: 9, delay: 2.4, repeat: Infinity, repeatDelay: 6, times: [0, 0.15, 0.85, 1] },
      },
    },

    // Falling leaves
    {
      el: <IconLeaf size={36} tint="#5F7A5A" />,
      style: { top: 0, left: "18%" },
      anim: {
        animate: {
          y: [-40, 380, 500],
          opacity: [0, 1, 0],
          rotate: [-10, 220, 340],
          x: [0, -20, 40],
        },
        transition: { duration: 10, delay: 4, repeat: Infinity, repeatDelay: 4, ease: "easeInOut" },
      },
    },
    {
      el: <IconLeaf size={28} tint="#3E5B3A" />,
      style: { top: 0, right: "22%" },
      anim: {
        animate: {
          y: [-30, 400, 520],
          opacity: [0, 1, 0],
          rotate: [12, -180, -320],
          x: [0, 40, -20],
        },
        transition: { duration: 12, delay: 8, repeat: Infinity, repeatDelay: 5, ease: "easeInOut" },
      },
    },

    // Yarn ball rolling
    {
      el: <IconYarnBall size={38} />,
      style: { bottom: "10%", left: "-6%" },
      anim: {
        animate: {
          x: ["0vw", "60vw", "110vw"],
          rotate: [0, 720, 1440],
          opacity: [0, 1, 0],
        },
        transition: { duration: 10, delay: 5, repeat: Infinity, repeatDelay: 12, ease: "easeInOut" },
      },
    },

    // Peeking cat tail from right edge
    {
      el: <IconCatTail />,
      style: { top: "62%", right: "-30px" },
      anim: {
        animate: {
          x: [80, -20, 80],
          opacity: [0, 1, 0],
          rotate: [-6, 6, -6],
        },
        transition: { duration: 5, delay: 6, repeat: Infinity, repeatDelay: 10 },
      },
    },

    // Map pin appearing along the route
    {
      el: <IconMapPin size={26} />,
      style: { top: "48%", left: "70%" },
      anim: popCycle(6, 2, 5.5),
    },

    // Drifting cloud
    {
      el: <IconCloud size={140} />,
      style: { top: "6%", left: "-10%" },
      anim: {
        animate: { x: ["0vw", "115vw"], opacity: [0, 0.55, 0] },
        transition: { duration: 26, delay: 3, repeat: Infinity, ease: "linear" },
      },
    },
  ];

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {doodles.map((d, i) => (
        <motion.div
          key={i}
          initial={{ opacity: 0 }}
          animate={d.anim.animate}
          transition={d.anim.transition}
          style={{ position: "absolute", ...d.style }}
        >
          {d.el}
        </motion.div>
      ))}
    </div>
  );
}
